import { Link, useLocation } from "wouter";
import { useGetCart, getGetCartQueryKey, useGetSettings } from "@workspace/api-client-react";
import { SOCIAL_ICON_PATHS } from "./StoreFooter";
import { getCartSessionId, SIDE_CART_OPEN_EVENT } from "@/lib/cart";
import { useAuth } from "@/lib/auth-context";
import { useWishlist } from "@/lib/wishlist-context";
import { useI18n } from "@/lib/i18n";
import { ShoppingCart, Menu, User, LogIn, Heart, Globe, Search, X, Phone, Bell, Package, Clock, Star, Settings, LogOut, UserCircle2, Home, Tag, Layers, Mail, Newspaper, MessageCircle, FileText, Wallet, Download, MapPin, Gift, Truck, Sparkles, Percent, Crown, Gem, Flame, Compass, Image as ImageIcon, Info, BookOpen, Headphones, Calendar, Award, Bookmark, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchAutocomplete } from "./SearchAutocomplete";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState, useEffect, useRef, lazy, Suspense, type CSSProperties } from "react";
import { MegaMenu } from "./MegaMenu";
import { useTheme } from "@/lib/theme-context";
import { withVersion, withWidthIfStorage, resolveImageSrc } from "@/lib/imgSrc";
import { useDarkMode } from "@/lib/dark-mode";
import { useCurrency, SUPPORTED_CURRENCIES } from "@/lib/currency";
import { Check, Moon, Sun } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Lazy: only loaded when user opens cart. MegaMenu is eager (imported above).
const SideCart = lazy(() => import("./SideCart").then(m => ({ default: m.SideCart })));

// Shared icon registry for the configurable header menu — keyed by
// stable string names persisted in each menu item's `icon` field. Keep
// names stable; renaming would orphan saved menu items. The admin
// "icon picker" in appearance.tsx imports MENU_ICON_NAMES below to
// build its dropdown so both sides stay in sync.
export const MENU_ICONS: Record<string, any> = {
  home: Home, shop: ShoppingCart, cart: ShoppingCart, user: User, account: User,
  heart: Heart, search: Search, phone: Phone, mail: Mail, message: MessageCircle,
  whatsapp: MessageCircle, package: Package, layers: Layers, star: Star,
  tag: Tag, newspaper: Newspaper, file: FileText, wallet: Wallet, download: Download,
  map: MapPin, gift: Gift, truck: Truck, sparkles: Sparkles, percent: Percent,
  crown: Crown, gem: Gem, flame: Flame, compass: Compass, image: ImageIcon,
  info: Info, book: BookOpen, headphones: Headphones, calendar: Calendar,
  award: Award, bookmark: Bookmark, bell: Bell, settings: Settings, globe: Globe,
};
export const MENU_ICON_NAMES = Object.keys(MENU_ICONS);

// Default icon NAME (key of MENU_ICONS) used per menu-item type when the
// merchant has not chosen an explicit icon. Kept in sync with the per-type
// defaults inside `resolveMenuItemRaw` below so the admin icon picker can
// show "Default (Star)" instead of a bare "Default" — merchants kept
// asking "where is the star I see in my drawer" because the picker never
// surfaced which icon the default actually resolved to.
export const TYPE_DEFAULT_ICON_NAME: Record<string, string> = {
  home: "home",
  shop: "shop",
  new: "star",
  offers: "tag",
  categories: "layers",
  brands: "star",
  wishlist: "heart",
  cart: "cart",
  account: "user",
  search: "search",
  blog: "newspaper",
  contact: "mail",
  whatsapp: "message",
  phone: "phone",
  custom: "tag",
  category: "layers",
  brand: "star",
  product: "package",
  page: "file",
  customPage: "file",
  accountOrders: "package",
  accountAddresses: "map",
  accountProfile: "user",
  accountBalance: "wallet",
  accountDownloads: "download",
  login: "user",
  register: "user",
};

// Whitelist for merchant-supplied custom menu URLs. Blocks XSS vectors like
// `javascript:`, `data:`, `vbscript:` while still allowing normal site links,
// internal SPA paths, and the contact protocols merchants commonly need
// (tel:, mailto:, sms:, whatsapp:). Anything that doesn't match an allowed
// shape is silently dropped at resolve time.
export function isSafeMenuUrl(url: string): boolean {
  if (typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("/") || trimmed.startsWith("#") || trimmed.startsWith("?")) return true;
  const lower = trimmed.toLowerCase();
  const allowed = ["http://", "https://", "tel:", "mailto:", "sms:", "whatsapp:", "wa.me/"];
  return allowed.some((p) => lower.startsWith(p));
}

export function StoreNavbar({ inStickyGroup = false }: { inStickyGroup?: boolean } = {}) {
  const [location, setLocation] = useLocation();
  const sessionId = getCartSessionId();
  const { customer, logoutCustomer } = useAuth();
  const { wishlistCount } = useWishlist();
  const { t, lang, setLang } = useI18n();
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Lazy-loaded category list used to nest sub-categories beneath each
  // category-type entry in the mobile drawer. Fetched the first time the
  // drawer is opened so the storefront's first paint isn't taxed.
  const [drawerCategories, setDrawerCategories] = useState<any[] | null>(null);
  // Track which parent categories have their sub-tree expanded in the
  // mobile drawer. Sub-trees start collapsed by design — the merchant
  // (and the user request) want the drawer to feel like a phone-app menu:
  // tap the chevron to reveal children, never auto-expand the whole tree.
  const [drawerExpanded, setDrawerExpanded] = useState<Record<string, boolean>>({});
  const toggleDrawerExpanded = (key: string) =>
    setDrawerExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const theme = useTheme();
  const cartDropdownStyle = (theme?.cartDropdownStyle as string) || "sidebar";
  const openCart = (close?: () => void) => {
    if (cartDropdownStyle === "page") {
      if (close) close();
      setLocation("/cart");
    } else {
      if (close) close();
      setCartOpen(true);
    }
  };
  // Only fetch the category tree when the drawer is open AND the merchant
  // hasn't disabled the sub-category feature. Avoids an unnecessary
  // round-trip on stores that don't surface categories in the drawer.
  const subsEnabled = (theme?.mobileDrawerShowSubcategories ?? "true") !== "false";
  useEffect(() => {
    if (!mobileMenuOpen || !subsEnabled || drawerCategories !== null) return;
    const BASE = (import.meta as any).env?.BASE_URL || "/";
    fetch(`${BASE}api/categories`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setDrawerCategories(Array.isArray(data) ? data : []))
      .catch(() => setDrawerCategories([]));
  }, [mobileMenuOpen, subsEnabled, drawerCategories]);
  const { isDark, toggle: toggleDark, adminEnabled: darkModeEnabled, showToggle: darkModeToggleVisible } = useDarkMode();
  const [shrunk, setShrunk] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  const showSearch = theme?.headerShowSearch !== "false";
  const showCart = theme?.headerShowCart !== "false";
  const showWishlist = theme?.headerShowWishlist !== "false";
  const showLanguage = theme?.headerShowLanguage !== "false";
  const showAccount = theme?.headerShowAccount !== "false";
  const showPhone = theme?.headerShowPhone === "true";
  const headerHeight = theme?.headerHeight ? `${String(theme.headerHeight).replace(/px$/i, "")}px` : "";
  const shrinkOnScroll = theme?.headerShrinkOnScroll === "true";
  const headerLogoSrc = theme?.headerLogoUrl || theme?.logoUrl;
  const usingCustomLogo = !!headerLogoSrc;
  // While theme settings are still being fetched, `theme` is null. Don't
  // render any logo source in that window — otherwise the built-in
  // fallback PNG flashes for a moment before being replaced by the
  // merchant's actual logo. Once theme is loaded we either show the
  // merchant logo or, if none is configured, the built-in fallback.
  const themeLoaded = theme !== null && theme !== undefined;
  const logoUrlRaw = (headerLogoSrc
    ? withVersion(headerLogoSrc, theme?.updatedAt)
    : (themeLoaded ? `${import.meta.env.BASE_URL}la-header-logo.png` : "")) as string;
  // Logo files uploaded by merchants are usually stored at their original
  // resolution (e.g. 696×166), but the header displays them at ~100×24.
  // Routing through `withWidthIfStorage(...,300)` asks the image proxy
  // for a ~300px-wide WebP variant — Retina-sharp at any header size we
  // currently support, and ~5× smaller bytes than the original PNG.
  // Pass-through (returns the original URL) for the built-in fallback PNG
  // and for any non-storage URL.
  // For merchant-uploaded storage URLs we ask the in-process image router
  // for a 300px variant; for external/R2 URLs we route through `/api/img-proxy`
  // (also at 300px) so they get resized + WebP/AVIF on the wire. Falls back
  // to the raw URL for the built-in fallback PNG.
  const logoUrl = (resolveImageSrc(logoUrlRaw, 300) || logoUrlRaw) as string;
  // The built-in fallback logo is a white/transparent PNG that needs to be
  // inverted on light backgrounds. Merchant-uploaded logos should render
  // as-is so their brand colors are preserved. The optional
  // `logoInvertOnLight` theme flag allows merchants to opt back into the
  // legacy inversion behavior if their custom logo happens to be white.
  const invertOnLight = !usingCustomLogo || theme?.logoInvertOnLight === "true";
  const logoFilter = isDark || !invertOnLight ? "none" : "invert(1)";
  const logoVisibility = logoUrl ? "visible" : "hidden";
  const contactPhone = theme?.contactPhone || "";
  const darkModeToggle = darkModeToggleVisible;
  const headerLayout = theme?.headerLayout || "default";
  const megaMenuEnabled = theme?.megaMenuEnabled !== "false" && theme?.megaMenuEnabled !== false;
  const megaMenuPosition = theme?.megaMenuPosition || "below";
  const megaMenuInline = megaMenuEnabled && megaMenuPosition === "inline";
  const megaMenuAbove = megaMenuEnabled && megaMenuPosition === "above";

  const zoneDefaults: Record<string, [string, string, string]> = {
    default:  ["logo", "nav", "actions"],
    centered: ["actions", "logo", "nav"],
    compact:  ["logo", "nav", "actions"],
    minimal:  ["logo", "empty", "actions"],
  };
  const [defStart, defCenter, defEnd] = zoneDefaults[headerLayout] || zoneDefaults.default;
  const resolveZone = (val: string | undefined, def: string) => (!val || val === "auto") ? def : val;
  const zoneStart = resolveZone(theme?.headerZoneStart, defStart);
  const zoneCenter = resolveZone(theme?.headerZoneCenter, defCenter);
  const zoneEnd = resolveZone(theme?.headerZoneEnd, defEnd);

  const { data: cart } = useGetCart(
    { sessionId },
    { query: { queryKey: getGetCartQueryKey({ sessionId }) } }
  );

  const cartItemCount = cart?.itemCount || 0;

  useEffect(() => {
    if (!shrinkOnScroll) return;
    const onScroll = () => setShrunk(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [shrinkOnScroll]);

  useEffect(() => {
    const onOpen = () => {
      if (cartDropdownStyle === "page") setLocation("/cart");
      else setCartOpen(true);
    };
    window.addEventListener(SIDE_CART_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SIDE_CART_OPEN_EVENT, onOpen);
  }, [cartDropdownStyle]);

  const handleLogout = async () => {
    await logoutCustomer();
    setLocation("/");
  };

  const currentHeaderHeight = shrinkOnScroll && shrunk
    ? (headerHeight ? `${Math.max(40, parseInt(headerHeight) - 30)}px` : "40px")
    : headerHeight;

  // ---- Proportional icon/button scaling based on the header height ----
  // Baseline header is ~64px → buttons 36px (h-9) and icons 18px. Larger
  // headers scale icons up proportionally so they don't look lost in tall
  // headers; smaller headers scale them down a bit. Clamped so things stay
  // tap-friendly on mobile and don't get absurdly huge.
  const baseHeaderH = 64;
  const effectiveHeaderH = currentHeaderHeight ? parseInt(currentHeaderHeight) : baseHeaderH;
  const iconScale = Math.max(0.85, Math.min(1.8, (effectiveHeaderH || baseHeaderH) / baseHeaderH));
  const actionBtnPx = Math.round(36 * iconScale);
  const actionIconPx = Math.round(18 * iconScale);
  const actionBadgePx = Math.round(16 * iconScale);
  const headerIconColor = theme?.headerIconColor || theme?.headerTextColor || undefined;
  const actionBtnStyle: React.CSSProperties = { width: actionBtnPx, height: actionBtnPx, color: headerIconColor };
  const actionIconStyle: React.CSSProperties = { width: actionIconPx, height: actionIconPx, color: headerIconColor };
  const actionBadgeStyle = { width: actionBadgePx, height: actionBadgePx, fontSize: Math.max(9, Math.round(10 * iconScale)) };
  // Logo also tracks header growth so the brand mark stays balanced.
  const baseLogoPx = headerLayout === "minimal" ? 18 : 22;
  const autoLogoHeightPx = shrinkOnScroll && shrunk ? 16 : Math.round(baseLogoPx * iconScale);
  // Merchant overrides (theme settings → Header → Logo Size). Empty / invalid → auto.
  const parsePx = (v: any): number | null => {
    if (v === undefined || v === null || v === "") return null;
    const n = parseInt(String(v).replace(/[^\d.-]/g, ""), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const logoMaxHpDesktop = parsePx(theme?.headerLogoMaxHeight);
  const logoMaxHpMobile  = parsePx(theme?.headerLogoMaxHeightMobile);
  const logoMaxWpDesktop = parsePx(theme?.headerLogoMaxWidth);
  const logoMaxWpMobile  = parsePx(theme?.headerLogoMaxWidthMobile);
  // Desktop value used for inline sizing (mobile is overridden via media query below).
  // Cap the logo height at the configured header height so increasing the logo
  // height never silently inflates the entire header row. The wrapper below
  // uses this value as its `height`, so anything larger than the header would
  // otherwise stretch the header to match. To grow the logo further the
  // merchant must also increase the header height.
  const requestedLogoHeightPx = logoMaxHpDesktop ?? autoLogoHeightPx;
  const headerHeightCap = headerHeight ? parseInt(headerHeight) : null;
  const logoHeightPx = headerHeightCap
    ? Math.min(requestedLogoHeightPx, Math.max(16, headerHeightCap))
    : requestedLogoHeightPx;
  const logoMaxWidthPx = logoMaxWpDesktop ?? Math.round(logoHeightPx * 4.2);

  // ---- Configurable header menu items (desktop nav + mobile drawer) ----
  type MenuItem = {
    type: string;
    labelAr?: string;
    labelEn?: string;
    url?: string;
    desktop?: boolean;
    mobile?: boolean;
    targetId?: number;
    targetSlug?: string;
    icon?: string;
  };
  const rawMenuItems = theme?.headerMenuItems;
  const configuredMenuItems: MenuItem[] = Array.isArray(rawMenuItems) ? rawMenuItems : [];
  const hasConfiguredMenu = configuredMenuItems.length > 0;
  const phoneForMenu = theme?.contactPhone || "";
  const whatsappForMenu = (theme?.whatsappPhone || theme?.contactPhone || "").toString().replace(/[^\d+]/g, "");

  const resolveMenuItemRaw = (it: MenuItem): { href: string; label: string; icon: any } | null => {
    const lAr = it.labelAr || "";
    const lEn = it.labelEn || "";
    const labelOf = (defAr: string, defEn: string) =>
      lang === "ar" ? (lAr || defAr) : (lEn || defEn);
    switch (it.type) {
      case "home":       return { href: "/",                     label: labelOf("الرئيسية", "Home"),         icon: Home };
      case "shop":       return { href: "/shop",                 label: labelOf("تسوق",     t.nav.shopAll),  icon: ShoppingCart };
      case "new":        return { href: "/new-arrivals",         label: labelOf("وصل حديثاً","New Arrivals"), icon: Star };
      case "offers":     return { href: "/shop?category=sale",   label: labelOf("العروض",   "Offers"),       icon: Tag };
      case "categories": return { href: "/shop",                 label: labelOf("التصنيفات","Categories"),   icon: Layers };
      case "brands":     return { href: "/brands",               label: labelOf("الماركات", "Brands"),       icon: Star };
      case "wishlist":   return { href: "/wishlist",             label: labelOf("المفضلة",  t.nav.wishlist), icon: Heart };
      case "cart":       return { href: "#open-side-cart",       label: labelOf("السلة",    "Cart"),         icon: ShoppingCart };
      case "account":    return { href: "/account",              label: labelOf("حسابي",    t.nav.myAccount),icon: User };
      case "search":     return { href: "/search",               label: labelOf("البحث",    "Search"),       icon: Search };
      case "blog":       return { href: "/blog",                 label: labelOf("المدونة",  "Blog"),         icon: Newspaper };
      case "contact":    return { href: "/contact",              label: labelOf("تواصل",    t.nav.contactUs),icon: Mail };
      case "whatsapp":   return whatsappForMenu
        ? { href: `https://wa.me/${whatsappForMenu.replace(/^\+/, "")}`, label: labelOf("واتساب", "WhatsApp"), icon: MessageCircle }
        : null;
      case "phone":      return phoneForMenu
        ? { href: `tel:${phoneForMenu}`, label: labelOf("اتصل بنا", "Call us"), icon: Phone }
        : null;
      case "custom":     return it.url && isSafeMenuUrl(it.url)
        ? { href: it.url, label: labelOf(lAr || lEn || "—", lEn || lAr || "—"), icon: Tag }
        : null;
      // Specific-entity types — populated from the admin "target picker".
      // Resolution to the storefront URL lives here (not in the admin) so
      // we stay free to change URL conventions without re-saving items.
      case "category":   return it.targetId
        ? { href: `/category/${it.targetId}`,         label: labelOf(lAr || lEn || "—", lEn || lAr || "—"), icon: Layers }
        : null;
      case "brand":      return it.targetId
        ? { href: `/shop?brand=${it.targetId}`,       label: labelOf(lAr || lEn || "—", lEn || lAr || "—"), icon: Star }
        : null;
      case "product":    return it.targetId
        ? { href: `/product/${it.targetId}`,          label: labelOf(lAr || lEn || "—", lEn || lAr || "—"), icon: Package }
        : null;
      case "page":       return it.targetSlug
        ? { href: `/page/${it.targetSlug}`,           label: labelOf(lAr || lEn || "—", lEn || lAr || "—"), icon: FileText }
        : null;
      case "customPage": return it.targetSlug
        ? { href: `/p/${it.targetSlug}`,              label: labelOf(lAr || lEn || "—", lEn || lAr || "—"), icon: FileText }
        : null;
      // Account sub-tabs — the storefront /account page reads ?tab=...
      // and supports: orders, balance, downloads, profile, addresses,
      // password (see ACCOUNT_TABS in pages/account.tsx).
      case "accountOrders":    return { href: "/account?tab=orders",    label: labelOf("الطلبات",   "Orders"),    icon: Package };
      case "accountAddresses": return { href: "/account?tab=addresses", label: labelOf("العناوين",  "Addresses"), icon: MapPin };
      case "accountProfile":   return { href: "/account?tab=profile",   label: labelOf("الملف",     "Profile"),   icon: User };
      case "accountBalance":   return { href: "/account?tab=balance",   label: labelOf("رصيدي",     "Balance"),   icon: Wallet };
      case "accountDownloads": return { href: "/account?tab=downloads", label: labelOf("التحميلات", "Downloads"), icon: Download };
      // Auth links — first-class menu items so the merchant can place them
      // anywhere in the drawer and override their icon/label/order. The
      // legacy show-login/show-register fallback below the menu is only
      // used when neither type appears in the configured items.
      case "login":      return { href: "/login",                label: labelOf("تسجيل الدخول", t.nav.signIn),       icon: User };
      case "register":   return { href: "/register",             label: labelOf("إنشاء حساب",   t.nav.createAccount), icon: UserCircle2 };
      default: return null;
    }
  };

  // Wrap raw resolver so a merchant-chosen icon (it.icon) overrides the
  // default per-type icon. Unknown / unset names fall back to the default.
  // Preserve the raw menu-item metadata (type / targetId / targetSlug)
  // alongside the resolved href+label so the mobile drawer can look up
  // sub-categories for "category"-type entries. Desktop rendering ignores
  // the extra fields.
  type ResolvedMenuItem = {
    href: string;
    label: string;
    icon: any;
    type?: string;
    targetId?: number | null;
    targetSlug?: string | null;
  };
  const resolveMenuItem = (it: MenuItem): ResolvedMenuItem | null => {
    const base = resolveMenuItemRaw(it);
    if (!base) return null;
    const override = it.icon ? MENU_ICONS[it.icon] : null;
    const merged = override ? { ...base, icon: override } : base;
    return {
      ...merged,
      type: (it as any).type,
      targetId: (it as any).targetId ?? null,
      targetSlug: (it as any).targetSlug ?? null,
    };
  };

  const desktopMenuItems = configuredMenuItems
    .filter((it) => it.desktop !== false)
    .map(resolveMenuItem)
    .filter((x): x is ResolvedMenuItem => !!x);

  // Mobile drawer items: prefer the dedicated `mobileDrawerMenuItems` theme
  // field (managed at /admin/mobile-menu) when it has at least one entry —
  // it gives the merchant a fully independent mobile menu without forcing
  // them to share the desktop header items. Falls back to the shared
  // `headerMenuItems` (filtered by mobile flag) for legacy stores.
  const rawMobileDrawerItems = (theme as any)?.mobileDrawerMenuItems;
  const mobileDrawerOverride: MenuItem[] = Array.isArray(rawMobileDrawerItems) ? rawMobileDrawerItems : [];
  const mobileMenuItems = (mobileDrawerOverride.length > 0
    ? mobileDrawerOverride
    : configuredMenuItems.filter((it) => it.mobile !== false)
  )
    .map(resolveMenuItem)
    .filter((x): x is ResolvedMenuItem => !!x);

  const renderMenuLink = (m: { href: string; label: string }, key: string | number, className: string) => {
    if (m.href === "#open-side-cart") {
      return (
        <button
          type="button"
          key={key}
          className={className}
          onClick={(e) => { e.preventDefault(); openCart(); }}
        >
          {m.label}
        </button>
      );
    }
    const isExternal = /^(https?:|tel:|mailto:)/i.test(m.href);
    if (isExternal) {
      return <a key={key} href={m.href} target={m.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={className}>{m.label}</a>;
    }
    return <Link key={key} href={m.href} className={className}>{m.label}</Link>;
  };

  const renderMobileMenu = () => {
    const menuBg = (theme?.mobileMenuBg || "").toString().trim() || undefined;
    const menuTextColor = (theme?.mobileMenuTextColor || "").toString().trim() || undefined;
    const menuBorderColor = (theme?.mobileMenuBorderColor || "").toString().trim() || undefined;
    const menuLogoTint = (theme?.mobileMenuLogoColor || "").toString().trim();
    // To recolor a transparent PNG/SVG logo to any solid hex we use a CSS
    // `mask-image` over a background-color block — the previous
    // `drop-shadow(0 0 0 ...)` trick rendered the logo black with an invisible
    // tint underneath, so the chosen color was silently ignored. The mask
    // technique applies the merchant's color exactly while preserving the
    // logo's alpha channel.
    // Prefer the drawer-specific logo (mobileDrawerLogoUrl) for the mask
    // so a merchant who uploads a dedicated drawer logo gets it tinted too.
    const drawerLogoSrc = theme?.mobileDrawerLogoUrl
      ? withVersion(theme.mobileDrawerLogoUrl, theme?.updatedAt)
      : logoUrl;
    const maskedLogoStyle: React.CSSProperties | null = menuLogoTint && drawerLogoSrc
      ? {
          display: "block",
          width: 120,
          height: 32,
          backgroundColor: menuLogoTint,
          WebkitMaskImage: `url(${JSON.stringify(drawerLogoSrc)})`,
          maskImage: `url(${JSON.stringify(drawerLogoSrc)})`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          // Always anchor the masked logo to the START edge of its box
          // (left in LTR, right in RTL) so it lines up flush with the
          // category labels below, which use `text-start`.
          WebkitMaskPosition: lang === "ar" ? "right center" : "left center",
          maskPosition: lang === "ar" ? "right center" : "left center",
          WebkitMaskSize: "contain",
          maskSize: "contain",
          visibility: logoVisibility,
        }
      : null;
    // Group wrapper carries the divider; the parent link itself is NOT
    // borderless — this way when a category has children, the chevron-
    // expanded sub-list sits ABOVE the divider, visually attached to its
    // parent. Previously the parent had its own border-b and the subs
    // rendered below it, making them look like they belonged to the
    // *next* category. Fix per merchant feedback.
    const groupClass = "border-b";
    const groupStyle: React.CSSProperties = { borderColor: menuBorderColor };
    const linkClassBase = "text-base font-bold tracking-wide transition-colors py-4 text-start";
    const linkStyle: React.CSSProperties = { color: menuTextColor };
    const lastLinkStyle: React.CSSProperties = { color: menuTextColor };
    const subLinkClass = "text-sm font-medium transition-colors py-2 ps-5 block";
    const showLogin = (theme?.mobileDrawerShowLogin ?? "true") !== "false";
    const showRegister = (theme?.mobileDrawerShowRegister ?? "true") !== "false";
    const showSubs = (theme?.mobileDrawerShowSubcategories ?? "true") !== "false";
    // Build a quick id → children map for sub-category rendering.
    const childrenByParent = new Map<number, Array<{ id: number; slug?: string; nameAr?: string; nameEn?: string }>>();
    if (showSubs && Array.isArray(drawerCategories)) {
      for (const c of drawerCategories as any[]) {
        const pid = Number(c?.parentId);
        if (Number.isFinite(pid) && pid > 0) {
          if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
          childrenByParent.get(pid)!.push(c);
        }
      }
    }
    const renderInDrawer = (m: { href: string; label: string; icon?: any }, key: string | number) => {
      const IconCmp = m.icon || null;
      const iconNode = IconCmp ? (
        <IconCmp className="h-5 w-5 shrink-0" style={{ color: menuTextColor }} aria-hidden="true" />
      ) : null;
      if (m.href === "#open-side-cart") {
        return (
          <div key={key} className={groupClass} style={groupStyle}>
            <button
              type="button"
              className={`${linkClassBase} hover:text-foreground text-start w-full flex items-center gap-3`}
              style={linkStyle}
              onClick={(e) => { e.preventDefault(); openCart(() => setMobileMenuOpen(false)); }}
            >
              {iconNode}
              <span>{m.label}</span>
            </button>
          </div>
        );
      }
      const isExternal = /^(https?:|tel:|mailto:)/i.test(m.href);
      // Detect "category" menu items and pull their direct children so we
      // can render them as a nested, indented list right below the parent.
      // The drawer items come from /admin/mobile-menu where category entries
      // store either a targetId (preferred) or a targetSlug.
      const itemAny = m as any;
      const isCategoryItem = itemAny?.type === "category" && (itemAny?.targetId || itemAny?.targetSlug);
      let subs: any[] = [];
      if (showSubs && isCategoryItem && Array.isArray(drawerCategories)) {
        const parent = (drawerCategories as any[]).find((c) =>
          itemAny.targetId ? Number(c.id) === Number(itemAny.targetId)
                           : c.slug && c.slug === itemAny.targetSlug,
        );
        if (parent) subs = childrenByParent.get(Number(parent.id)) || [];
      }
      const hasSubs = subs.length > 0;
      const expandKey = String(key);
      const isExpanded = !!drawerExpanded[expandKey];
      const chevron = hasSubs ? (
        <button
          type="button"
          aria-label={lang === "ar" ? (isExpanded ? "إخفاء الأقسام الفرعية" : "إظهار الأقسام الفرعية") : (isExpanded ? "Hide sub-categories" : "Show sub-categories")}
          aria-expanded={isExpanded}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDrawerExpanded(expandKey); }}
          className="ms-auto p-2 -my-2 hover:opacity-70 transition-opacity"
          style={{ color: menuTextColor }}
          data-testid={`button-toggle-subs-${expandKey}`}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
      ) : null;
      const renderSubs = () => (hasSubs && isExpanded) ? (
        <div className="flex flex-col pb-2" data-testid={`subs-${expandKey}`}>
          {subs.map((c) => {
            const label = lang === "ar" ? (c.nameAr || c.nameEn) : (c.nameEn || c.nameAr);
            return (
              <Link
                key={`sub-${c.id}`}
                href={`/shop?category=${c.slug || c.id}`}
                className={`${subLinkClass} hover:opacity-80`}
                style={{ color: menuTextColor, opacity: 0.85 }}
                onClick={() => setMobileMenuOpen(false)}
              >
                — {label}
              </Link>
            );
          })}
        </div>
      ) : null;
      // Parent row: link + (optional) chevron, sharing a single flex row so
      // the chevron sits on the trailing edge across LTR and RTL.
      const parentRow = (() => {
        const linkCls = `${linkClassBase} hover:text-foreground flex flex-1 items-center gap-3`;
        if (isExternal) {
          return (
            <a
              href={m.href}
              target={m.href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className={linkCls}
              style={linkStyle}
              onClick={() => setMobileMenuOpen(false)}
            >
              {iconNode}
              <span>{m.label}</span>
            </a>
          );
        }
        return (
          <Link
            href={m.href}
            className={linkCls}
            style={linkStyle}
            onClick={() => setMobileMenuOpen(false)}
          >
            {iconNode}
            <span>{m.label}</span>
          </Link>
        );
      })();
      return (
        <div key={key} className={groupClass} style={groupStyle}>
          <div className="flex items-center">
            {parentRow}
            {chevron}
          </div>
          {renderSubs()}
        </div>
      );
    };
    return (
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="hover:bg-transparent" style={actionBtnStyle} aria-label={lang === "ar" ? "القائمة" : "Menu"}>
            <Menu style={actionIconStyle} aria-hidden="true" />
          </Button>
        </SheetTrigger>
        <SheetContent side={lang === "ar" ? "right" : "left"} className="pe-0 bg-background w-80 overflow-y-auto max-h-screen" style={{ backgroundColor: menuBg, color: menuTextColor }}>
          {/* Logo container shares the exact same horizontal padding (px-2)
              as the <nav> below so the logo's start edge lines up with the
              start edge of the category labels. No `gap` — the logo is the
              only child, and a residual gap would push it off the start
              edge in some flex layouts. */}
          <div className="flex items-center mb-8 px-2 justify-start">
            {maskedLogoStyle ? (
              <span
                role="img"
                aria-label={theme?.storeName || ""}
                className="h-8 w-auto"
                style={maskedLogoStyle}
              />
            ) : (
              (() => {
                // Drawer can have its own dedicated logo. Falls back to the
                // regular header logoUrl so existing stores keep working.
                const drawerSrc = theme?.mobileDrawerLogoUrl
                  ? withVersion(theme.mobileDrawerLogoUrl, theme?.updatedAt)
                  : logoUrl;
                return (
                  <img
                    src={drawerSrc}
                    alt={theme?.storeName || ""}
                    width={120}
                    height={32}
                    className="h-8 w-auto"
                    style={{ filter: logoFilter, visibility: drawerSrc ? "visible" : "hidden" }}
                  />
                );
              })()
            )}
          </div>
          <nav className="flex flex-col gap-4 px-2">
            {mobileMenuItems.length > 0 ? (
              mobileMenuItems.map((m, idx) => renderInDrawer(m, idx))
            ) : (
              <>
                <Link href="/shop" className={`${linkClassBase} hover:text-foreground`} style={linkStyle} onClick={() => setMobileMenuOpen(false)}>{t.nav.shopAll}</Link>
                <Link href="/new-arrivals" className={`${linkClassBase} hover:text-foreground`} style={linkStyle} onClick={() => setMobileMenuOpen(false)}>{lang === "ar" ? "وصل حديثاً" : "New Arrivals"}</Link>
                <Link href="/shop?category=sale" className={`${linkClassBase} hover:text-foreground`} style={linkStyle} onClick={() => setMobileMenuOpen(false)}>{lang === "ar" ? "العروض" : "Offers"}</Link>
                <Link href="/blog" className={`${linkClassBase} hover:text-foreground`} style={linkStyle} onClick={() => setMobileMenuOpen(false)}>{lang === "ar" ? "المدونة" : "Blog"}</Link>
                <Link href="/contact" className={`${linkClassBase} hover:text-foreground`} style={linkStyle} onClick={() => setMobileMenuOpen(false)}>{t.nav.contactUs}</Link>
              </>
            )}
            {customer ? (
              <>
                <div className={groupClass} style={groupStyle}>
                  <Link href="/account" className={`${linkClassBase} hover:text-foreground block`} style={linkStyle} onClick={() => setMobileMenuOpen(false)}>{t.nav.myAccount}</Link>
                </div>
                <div className={groupClass} style={groupStyle}>
                  <Link href="/wishlist" className={`${linkClassBase} hover:text-foreground block`} style={linkStyle} onClick={() => setMobileMenuOpen(false)}>{t.nav.wishlist}</Link>
                </div>
                <button onClick={() => { setMobileMenuOpen(false); handleLogout(); }} className="text-start text-sm tracking-widest uppercase hover:text-foreground transition-colors py-2" style={lastLinkStyle}>{t.nav.signOut}</button>
              </>
            ) : (
              <>
                {/* Auth links: when the merchant has placed `login` or
                    `register` items directly in the configurable mobile
                    menu (via /admin/mobile-menu) we skip the legacy
                    fallback row — they now control icon, label and
                    position from one place. The boolean toggles still
                    serve as a global hide. */}
                {showLogin && !mobileMenuItems.some((it) => (it as any).type === "login") && (
                  <div className={groupClass} style={groupStyle}>
                    <Link href="/login" className={`${linkClassBase} hover:text-foreground block`} style={linkStyle} onClick={() => setMobileMenuOpen(false)}>{t.nav.signIn}</Link>
                  </div>
                )}
                {showRegister && !mobileMenuItems.some((it) => (it as any).type === "register") && (
                  <div className={groupClass} style={groupStyle}>
                    <Link href="/register" className={`${linkClassBase} hover:text-foreground block`} style={linkStyle} onClick={() => setMobileMenuOpen(false)}>{t.nav.createAccount}</Link>
                  </div>
                )}
              </>
            )}
          </nav>
          <MobileDrawerExtras lang={lang} setLang={setLang} onClose={() => setMobileMenuOpen(false)} theme={theme} menuTextColor={menuTextColor} />
        </SheetContent>
      </Sheet>
    );
  };

  const renderActionButtons = () => (
    <div className="flex items-center gap-0.5">
      {showSearch && theme?.searchStyle === "inline" ? (
        <div className="hidden md:block w-56 lg:w-72 me-2">
          <SearchAutocomplete />
        </div>
      ) : null}
      {showSearch && theme?.searchStyle !== "inline" && (
        <Button variant="ghost" size="icon" className="hover:bg-gray-50 dark:hover:bg-gray-800" style={actionBtnStyle} onClick={() => setSearchOpen(!searchOpen)} aria-label={searchOpen ? (lang === "ar" ? "إغلاق البحث" : "Close search") : (lang === "ar" ? "بحث" : "Search")} aria-expanded={searchOpen}>
          {searchOpen ? <X style={actionIconStyle} aria-hidden="true" /> : <Search style={actionIconStyle} aria-hidden="true" />}
        </Button>
      )}
      {showAccount && (
        customer ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-gray-50" style={actionBtnStyle} aria-label={lang === "ar" ? "حسابي" : "My account"}><User style={actionIconStyle} aria-hidden="true" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <div className="px-3 py-2.5 flex items-center gap-2.5 border-b">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <UserCircle2 className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{customer.firstName} {customer.lastName}</div>
                  {customer.email && <div className="text-xs text-muted-foreground truncate">{customer.email}</div>}
                </div>
              </div>
              <div className="py-1">
                <DropdownMenuItem asChild>
                  <Link href="/account?tab=notifications" className="cursor-pointer flex items-center gap-2">
                    <Bell className="h-4 w-4" /> {lang === "ar" ? "الإشعارات" : "Notifications"}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/account?tab=orders" className="cursor-pointer flex items-center gap-2">
                    <Package className="h-4 w-4" /> {t.nav.myOrders}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/account?tab=pending" className="cursor-pointer flex items-center gap-2">
                    <Clock className="h-4 w-4" /> {lang === "ar" ? "طلبات بانتظار الدفع" : "Pending Payment"}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/wishlist" className="cursor-pointer flex items-center gap-2">
                    <Star className="h-4 w-4" /> {lang === "ar" ? "المفضلات" : "Favorites"}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/account" className="cursor-pointer flex items-center gap-2">
                    <UserCircle2 className="h-4 w-4" /> {t.nav.myAccount}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/account?tab=settings" className="cursor-pointer flex items-center gap-2">
                    <Settings className="h-4 w-4" /> {lang === "ar" ? "الإعدادات" : "Settings"}
                  </Link>
                </DropdownMenuItem>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive flex items-center gap-2">
                <LogOut className="h-4 w-4" /> {t.nav.signOut}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button variant="ghost" size="icon" className="hover:bg-gray-50 dark:hover:bg-gray-800" style={actionBtnStyle} onClick={() => { const returnTo = encodeURIComponent(window.location.pathname + window.location.search); window.location.href = `/login?returnTo=${returnTo}`; }} title={t.nav.signIn} aria-label={t.nav.signIn}>
            <User style={actionIconStyle} aria-hidden="true" />
          </Button>
        )
      )}
      {showCart && (
        <Button variant="ghost" size="icon" className="relative hover:bg-gray-50" style={actionBtnStyle} onClick={() => openCart()} aria-label={`${lang === "ar" ? "السلة" : "Cart"}${cartItemCount > 0 ? ` (${cartItemCount})` : ""}`}>
          <ShoppingCart style={actionIconStyle} aria-hidden="true" />
          {cartItemCount > 0 && theme?.cartCountBadge !== "false" && (
            <Badge className="absolute -end-0.5 -top-0.5 justify-center rounded-full p-0 bg-black text-white" style={actionBadgeStyle}>{cartItemCount}</Badge>
          )}
        </Button>
      )}
    </div>
  );

  // Mobile-only overrides via injected media-query style. We rely on a stable
  // class name (`nx-header-logo`) on the wrapper + img so we can override the
  // inline desktop sizing on small screens without re-rendering on resize.
  // Same cap as desktop: never let the mobile logo height exceed the
  // configured header height — otherwise the header row stretches to fit.
  const cappedMobileLogoH = logoMaxHpMobile && headerHeightCap
    ? Math.min(logoMaxHpMobile, Math.max(16, headerHeightCap))
    : logoMaxHpMobile;
  const mobileLogoCss = (cappedMobileLogoH || logoMaxWpMobile)
    ? `@media (max-width: 767px){
        .nx-header-logo-wrap{${cappedMobileLogoH ? `height:${cappedMobileLogoH}px !important;` : ""}${logoMaxWpMobile ? `min-width:${logoMaxWpMobile}px !important;max-width:${logoMaxWpMobile}px !important;` : ""}}
        .nx-header-logo-img{${cappedMobileLogoH ? `height:${cappedMobileLogoH}px !important;` : ""}${logoMaxWpMobile ? `max-width:${logoMaxWpMobile}px !important;` : ""}}
      }`
    : "";
  // Optional bilingual text label next to the logo (e.g. brand wordmark when
  // the logo image is just a monogram).
  const logoLabelVisible = theme?.headerLogoLinkVisible === "true";
  const logoLabelText = logoLabelVisible
    ? (lang === "ar"
        ? ((theme?.headerLogoLinkTextAr as string) || (theme?.headerLogoLinkText as string) || "")
        : ((theme?.headerLogoLinkText as string) || (theme?.headerLogoLinkTextAr as string) || ""))
    : "";
  const LogoLabel = logoLabelText
    ? <span className="ms-2 text-sm font-semibold tracking-wide truncate" style={{ color: theme?.headerTextColor || undefined }}>{logoLabelText}</span>
    : null;

  const renderLogoZone = () => (
    <>
      {mobileLogoCss && <style>{mobileLogoCss}</style>}
      {theme?.headerLogoLink === "false" ? (
        <div className="nx-header-logo-wrap flex items-center shrink-0" style={{ minWidth: `${logoMaxWidthPx}px`, height: `${logoHeightPx}px` }}>
          <img src={logoUrl} alt={theme?.storeName || "Store"} width={logoMaxWidthPx} height={logoHeightPx} className="nx-header-logo-img transition-all duration-300 dark:brightness-100 w-auto h-full max-w-full object-contain object-start" style={{ filter: logoFilter, visibility: logoVisibility, maxWidth: `${logoMaxWidthPx}px` }} />
          {LogoLabel}
        </div>
      ) : (
        <Link href={(theme?.headerLogoLinkUrl as string) || "/"} className="nx-header-logo-wrap flex items-center shrink-0" style={{ minWidth: `${logoMaxWidthPx}px`, height: `${logoHeightPx}px` }}>
          <img src={logoUrl} alt={theme?.storeName || "Store"} width={logoMaxWidthPx} height={logoHeightPx} className="nx-header-logo-img transition-all duration-300 dark:brightness-100 w-auto h-full max-w-full object-contain object-start" style={{ filter: logoFilter, visibility: logoVisibility, maxWidth: `${logoMaxWidthPx}px` }} />
          {LogoLabel}
        </Link>
      )}
    </>
  );

  const renderNavZone = () => (
    <div className="flex items-center gap-6 min-w-0 text-[11px] font-medium tracking-[0.12em] uppercase">
      {megaMenuInline && (
        <div className="flex justify-center min-w-0">
          <MegaMenu inline />
        </div>
      )}
      {hasConfiguredMenu ? (
        desktopMenuItems.length > 0 && (() => {
          const linkColor = (theme?.headerQuickLinksColor || theme?.headerTextColor || "").toString().trim();
          const linkHover = (theme?.headerQuickLinksHoverColor || "").toString().trim();
          const useCustom = !!(linkColor || linkHover);
          const linkClass = useCustom
            ? "transition-colors whitespace-nowrap hover:opacity-70"
            : "text-gray-500 hover:text-black dark:hover:text-white transition-colors whitespace-nowrap";
          const linkStyle = useCustom && linkColor ? { color: linkColor } : undefined;
          const onEnter = useCustom && linkHover
            ? (e: React.MouseEvent<HTMLAnchorElement>) => { (e.currentTarget as HTMLAnchorElement).style.color = linkHover; }
            : undefined;
          const onLeave = useCustom && linkHover && linkColor
            ? (e: React.MouseEvent<HTMLAnchorElement>) => { (e.currentTarget as HTMLAnchorElement).style.color = linkColor; }
            : undefined;
          return (
            <div className="flex items-center gap-6">
              {desktopMenuItems.map((m, idx) => (
                <a
                  key={idx}
                  href={m.href}
                  className={linkClass}
                  style={linkStyle}
                  onMouseEnter={onEnter}
                  onMouseLeave={onLeave}
                  onClick={(e) => { e.preventDefault(); setLocation(m.href); }}
                >
                  {m.label}
                </a>
              ))}
            </div>
          );
        })()
      ) : (
        (theme?.headerQuickLink1Visible !== "false" || theme?.headerQuickLink2Visible !== "false") && (() => {
          const qlColor = (theme?.headerQuickLinksColor || theme?.headerTextColor || "").toString().trim();
          const qlHover = (theme?.headerQuickLinksHoverColor || "").toString().trim();
          const useCustom = !!(qlColor || qlHover);
          const baseClass = useCustom
            ? "transition-colors whitespace-nowrap hover:opacity-70"
            : "text-gray-500 hover:text-black dark:hover:text-white transition-colors whitespace-nowrap";
          const baseStyle = useCustom ? { color: qlColor || undefined } : undefined;
          const onEnter = useCustom && qlHover
            ? (e: React.MouseEvent<HTMLAnchorElement>) => { (e.currentTarget as HTMLAnchorElement).style.color = qlHover; }
            : undefined;
          const onLeave = useCustom
            ? (e: React.MouseEvent<HTMLAnchorElement>) => { (e.currentTarget as HTMLAnchorElement).style.color = qlColor || ""; }
            : undefined;
          return (
            <div className="flex items-center gap-6">
              {theme?.headerQuickLink1Visible !== "false" && (
                <Link href={theme?.headerQuickLink1Url || "/new-arrivals"} className={baseClass} style={baseStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>
                  {lang === "ar" ? (theme?.headerQuickLink1TextAr || "وصل حديثاً") : (theme?.headerQuickLink1Text || "New Arrivals")}
                </Link>
              )}
              {theme?.headerQuickLink2Visible !== "false" && (
                <Link href={theme?.headerQuickLink2Url || "/shop?category=sale"} className={baseClass} style={baseStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>
                  {lang === "ar" ? (theme?.headerQuickLink2TextAr || "العروض") : (theme?.headerQuickLink2Text || "Offers")}
                </Link>
              )}
            </div>
          );
        })()
      )}
    </div>
  );

  const renderActionsZone = () => (
    <div className="flex items-center gap-1">
      {showPhone && contactPhone && (() => {
        const phoneColor = (theme?.headerIconColor || theme?.headerTextColor || "").toString().trim();
        const phoneHover = (theme?.headerQuickLinksHoverColor || "").toString().trim();
        const phoneStyle: React.CSSProperties = phoneColor ? { color: phoneColor } : {};
        const onEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
          if (phoneHover) (e.currentTarget as HTMLAnchorElement).style.color = phoneHover;
        };
        const onLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
          if (phoneColor) (e.currentTarget as HTMLAnchorElement).style.color = phoneColor;
        };
        return (
          <a
            href={`tel:${contactPhone}`}
            className="hidden md:flex items-center gap-1 text-xs me-2 hover:opacity-70 transition-opacity"
            style={phoneStyle}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
          >
            <Phone className="h-3.5 w-3.5" style={phoneColor ? { color: phoneColor, stroke: phoneColor } : undefined} />
            <span dir="ltr" style={phoneColor ? { color: phoneColor } : undefined}>{contactPhone}</span>
          </a>
        );
      })()}
      {showLanguage && <LangCurrencySwitcher lang={lang} setLang={setLang} btnStyle={actionBtnStyle} iconStyle={actionIconStyle} />}
      {renderActionButtons()}
      {darkModeEnabled && darkModeToggle && (
        <Button variant="ghost" size="icon" className="hover:bg-gray-50 dark:hover:bg-gray-800" style={actionBtnStyle} onClick={toggleDark} title={isDark ? "Light Mode" : "Dark Mode"} aria-label={isDark ? (lang === "ar" ? "الوضع الفاتح" : "Switch to light mode") : (lang === "ar" ? "الوضع الداكن" : "Switch to dark mode")} aria-pressed={isDark}>
          {isDark ? <Sun style={actionIconStyle} aria-hidden="true" /> : <Moon style={actionIconStyle} aria-hidden="true" />}
        </Button>
      )}
      {showWishlist && customer && theme?.wishlistEnabled !== "false" && (
        <Button variant="ghost" size="icon" className="relative hover:bg-gray-50" style={actionBtnStyle} asChild>
          <Link href="/wishlist" aria-label={`${lang === "ar" ? "المفضلة" : "Wishlist"}${wishlistCount > 0 ? ` (${wishlistCount})` : ""}`}>
            <Heart style={actionIconStyle} aria-hidden="true" />
            {wishlistCount > 0 && (
              <Badge className="absolute -end-0.5 -top-0.5 justify-center rounded-full p-0 bg-black text-white" style={actionBadgeStyle}>{wishlistCount}</Badge>
            )}
          </Link>
        </Button>
      )}
    </div>
  );

  const renderZone = (zone: string) => {
    switch (zone) {
      case "logo": return renderLogoZone();
      case "nav": return renderNavZone();
      case "actions": return renderActionsZone();
      default: return null;
    }
  };

  const pyClass = headerLayout === "compact" ? "py-0" : headerLayout === "minimal" ? "py-0.5" : "py-1";

  return (
    <>
      <header
        ref={headerRef}
        className={`${theme?.stickyHeader !== "false" && !inStickyGroup ? "sticky top-0" : ""} z-50 w-full bg-white dark:bg-gray-950 transition-all duration-300 store-header`}
        style={{
          backgroundColor: isDark ? undefined : (theme?.headerBg || "#fff"),
          color: isDark ? undefined : (theme?.headerTextColor || "#1e293b"),
          fontWeight: (theme?.headerFontWeight && theme.headerFontWeight !== "default") ? theme.headerFontWeight : undefined,
          ...((!isDark && theme?.headerIconHoverColor) ? { "--store-header-icon-hover": theme.headerIconHoverColor } as React.CSSProperties : {}),
        }}
      >
        {megaMenuAbove && (
          <div className="hidden xl:block border-b border-gray-100 dark:border-gray-800">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
              <MegaMenu inline />
            </div>
          </div>
        )}

        {/* Mobile header — fixed: hamburger | logo | actions */}
        <div
          className={`xl:hidden max-w-[1400px] mx-auto px-4 ${pyClass} grid grid-cols-[auto_1fr_auto] items-center gap-2 transition-[min-height] duration-300`}
          style={currentHeaderHeight ? { minHeight: currentHeaderHeight } : undefined}
        >
          <div>{renderMobileMenu()}</div>
          {mobileLogoCss && <style>{mobileLogoCss}</style>}
          {theme?.headerLogoLink === "false" ? (
            <div className="nx-header-logo-wrap flex items-center justify-self-center shrink-0" style={{ minWidth: `${Math.round(logoHeightPx * 3)}px`, height: `${logoHeightPx}px` }}>
              <img src={logoUrl} alt={theme?.storeName || "Store"} width={Math.round(logoHeightPx * 3)} height={logoHeightPx} fetchPriority="high" className="nx-header-logo-img transition-all duration-300 dark:brightness-100 w-auto h-full max-w-full object-contain" style={{ filter: logoFilter, visibility: logoVisibility }} />
            </div>
          ) : (
            <Link href={(theme?.headerLogoLinkUrl as string) || "/"} className="nx-header-logo-wrap flex items-center justify-self-center shrink-0" style={{ minWidth: `${Math.round(logoHeightPx * 3)}px`, height: `${logoHeightPx}px` }}>
              <img src={logoUrl} alt={theme?.storeName || "Store"} width={Math.round(logoHeightPx * 3)} height={logoHeightPx} fetchPriority="high" className="nx-header-logo-img transition-all duration-300 dark:brightness-100 w-auto h-full max-w-full object-contain" style={{ filter: logoFilter, visibility: logoVisibility }} />
            </Link>
          )}
          <div className="flex items-center gap-1">
            {renderActionButtons()}
          </div>
        </div>

        {/* Desktop header — 3 configurable zones */}
        <div
          className={`hidden xl:grid grid-cols-3 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 ${pyClass} items-center gap-4 transition-[min-height] duration-300`}
          style={currentHeaderHeight ? { minHeight: currentHeaderHeight } : undefined}
        >
          <div className="flex items-center justify-start">{renderZone(zoneStart)}</div>
          <div className="flex items-center justify-center">{renderZone(zoneCenter)}</div>
          <div className="flex items-center justify-end">{renderZone(zoneEnd)}</div>
        </div>

      </header>

      {searchOpen && showSearch && (
        <div className="border-b border-gray-100 py-3 px-4 bg-white dark:bg-gray-950 w-full sticky top-0 z-[10000]">
          <div className="container max-w-lg mx-auto">
            <SearchAutocomplete />
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        {cartOpen && <SideCart open={cartOpen} onOpenChange={setCartOpen} />}
      </Suspense>
    </>
  );
}

function LangCurrencySwitcher({ lang, setLang, btnStyle, iconStyle }: { lang: "ar" | "en"; setLang: (l: "ar" | "en") => void; btnStyle?: CSSProperties; iconStyle?: CSSProperties }) {
  const { currencyCode, setCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="hover:bg-gray-50 dark:hover:bg-gray-800"
          style={btnStyle ?? { width: 36, height: 36 }}
          title={lang === "ar" ? "اللغة والعملة" : "Language & Currency"}
          aria-label={lang === "ar" ? "اللغة والعملة" : "Language and currency"}
        >
          <Globe style={iconStyle ?? { width: 18, height: 18 }} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-0"
        dir={lang === "ar" ? "rtl" : "ltr"}
      >
        <div className="p-3 border-b">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            {lang === "ar" ? "اللغة" : "Language"}
          </p>
          <div className="grid grid-cols-2 gap-1">
            {(["ar", "en"] as const).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => { setLang(code); }}
                className={`flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                  lang === code
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted"
                }`}
              >
                <span>{code === "ar" ? "العربية" : "English"}</span>
                {lang === code && <Check className="h-4 w-4" />}
              </button>
            ))}
          </div>
        </div>
        <div className="p-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            {lang === "ar" ? "العملة" : "Currency"}
          </p>
          <div className="max-h-64 overflow-y-auto space-y-0.5 -mx-1 px-1">
            {SUPPORTED_CURRENCIES.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => { setCurrency(c.code); setOpen(false); }}
                className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                  currencyCode === c.code
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-muted"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="font-mono text-xs w-10 text-start">{c.code}</span>
                  <span className="truncate">{lang === "ar" ? c.nameAr : c.nameEn}</span>
                </span>
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span>{c.symbol}</span>
                  {currencyCode === c.code && <Check className="h-4 w-4 text-primary" />}
                </span>
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MobileDrawerExtras({
  lang,
  setLang,
  onClose,
  theme,
  menuTextColor,
}: {
  lang: "ar" | "en";
  setLang: (l: "ar" | "en") => void;
  onClose: () => void;
  theme: any;
  menuTextColor: string;
}) {
  const { currencyCode, setCurrency } = useCurrency();
  const { data: settings } = useGetSettings();
  // Optional merchant-defined active-state colours for the pills below.
  // Fall back to the Tailwind theme primary when not set.
  const activeBg = (theme?.mobileMenuActiveBg || "").toString().trim();
  const activeText = (theme?.mobileMenuActiveText || "").toString().trim();
  const activePillStyle: React.CSSProperties | undefined =
    activeBg || activeText
      ? {
          backgroundColor: activeBg || undefined,
          color: activeText || undefined,
          borderColor: activeBg || undefined,
        }
      : undefined;
  const activePillClass = activePillStyle
    ? "border"
    : "bg-primary/10 text-primary border-primary";

  const enabled: string[] = Array.isArray((settings as any)?.enabledCurrencies)
    ? (settings as any).enabledCurrencies
    : [];
  const baseCode = (settings as any)?.currency || "SAR";
  const allowedSet = new Set<string>(
    enabled.length > 0 ? [baseCode, ...enabled] : [baseCode]
  );
  const currencyList = SUPPORTED_CURRENCIES.filter((c) => allowedSet.has(c.code));

  const socialLinks = [
    { type: "instagram", url: theme?.socialInstagram },
    { type: "facebook", url: theme?.socialFacebook },
    { type: "twitter", url: theme?.socialTwitter },
    { type: "youtube", url: theme?.socialYoutube },
    { type: "linkedin", url: theme?.socialLinkedin },
    { type: "tiktok", url: theme?.socialTiktok },
    { type: "snapchat", url: theme?.socialSnapchat },
    { type: "telegram", url: theme?.socialTelegram },
    { type: "pinterest", url: theme?.socialPinterest },
  ].filter((s) => s.url && String(s.url).trim().length > 0);

  const socialLabels: Record<string, { ar: string; en: string }> = {
    instagram: { ar: "إنستغرام", en: "Instagram" },
    facebook: { ar: "فيسبوك", en: "Facebook" },
    twitter: { ar: "إكس", en: "X" },
    youtube: { ar: "يوتيوب", en: "YouTube" },
    linkedin: { ar: "لينكدإن", en: "LinkedIn" },
    tiktok: { ar: "تيك توك", en: "TikTok" },
    snapchat: { ar: "سناب شات", en: "Snapchat" },
    telegram: { ar: "تيليجرام", en: "Telegram" },
    pinterest: { ar: "بنترست", en: "Pinterest" },
  };

  return (
    <div className="mt-6 px-2 space-y-5" style={{ color: menuTextColor }}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-2">
          {lang === "ar" ? "اللغة" : "Language"}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {(["ar", "en"] as const).map((code) => {
            const active = lang === code;
            return (
              <button
                key={code}
                type="button"
                onClick={() => setLang(code)}
                className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${
                  active ? activePillClass : "hover:bg-muted"
                }`}
                style={active ? activePillStyle : { borderColor: "currentColor", opacity: 0.85 }}
              >
                <span>{code === "ar" ? "العربية" : "English"}</span>
                {active && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      </div>

      {currencyList.length > 1 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-2">
            {lang === "ar" ? "العملة" : "Currency"}
          </p>
          <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
            {currencyList.map((c) => {
              const active = currencyCode === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => setCurrency(c.code)}
                  className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${
                    active ? activePillClass : "hover:bg-muted"
                  }`}
                  style={active ? activePillStyle : { borderColor: "currentColor", opacity: 0.85 }}
                  title={lang === "ar" ? c.nameAr : c.nameEn}
                >
                  <span className="font-mono text-xs">{c.code}</span>
                  <span className="text-sm">{c.symbol}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {socialLinks.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-2">
            {lang === "ar" ? "تابعنا" : "Follow Us"}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {socialLinks.map((s) => {
              const path = SOCIAL_ICON_PATHS[s.type];
              const label = socialLabels[s.type]?.[lang] || s.type;
              return (
                <a
                  key={s.type}
                  href={s.url as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  aria-label={label}
                  title={label}
                  className="inline-flex items-center justify-center rounded-full border w-10 h-10 transition-colors hover:bg-muted"
                  style={{ borderColor: "currentColor" }}
                >
                  {path ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d={path} />
                    </svg>
                  ) : (
                    <Globe className="h-4 w-4" />
                  )}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
